/**
 * DuerpExportPage - Page d'export PDF
 * Affiche le DUERP formaté pour impression via window.print()
 */

import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { DuerpExport, FREQUENCE_OPTIONS, VOIES_EXPOSITION_OPTIONS, EPC_OPTIONS, EPI_OPTIONS } from '../services/duerp'

export default function DuerpExportPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [exportData, setExportData] = useState<DuerpExport | null>(null)

  // Charger les données d'export depuis sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem('duerp_export')
    if (stored) {
      try {
        setExportData(JSON.parse(stored))
      } catch {
        navigate(`/duerp/${id}`)
      }
    } else {
      navigate(`/duerp/${id}`)
    }
  }, [id, navigate])

  // Lancer l'impression
  const handlePrint = () => {
    window.print()
  }

  if (!exportData) {
    return (
      <div className="flex items-center justify-center py-24">
        <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    )
  }

  const { document, synthese, mentions_legales, export_id, generated_at, tool_version } = exportData

  return (
    <>
      {/* Barre d'actions (masquée à l'impression) */}
      <div className="print:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link
            to={`/duerp/${id}`}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour
          </Link>
          <button onClick={handlePrint} className="btn-primary flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Imprimer / Enregistrer PDF
          </button>
        </div>
      </div>

      {/* Contenu du document */}
      <div className="print:pt-0 pt-20 max-w-4xl mx-auto px-4 print:px-0">
        {/* ========== PAGE 1: PAGE DE GARDE ========== */}
        <div className="print-page-break-after bg-white print:shadow-none shadow-lg rounded-lg print:rounded-none mb-8 print:mb-0">
          <div className="p-8 print:p-12">
            {/* En-tête */}
            <div className="text-center border-b-4 border-blue-600 pb-8 mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {mentions_legales.titre}
              </h1>
              <p className="text-sm text-gray-500">
                Document généré le {new Date(generated_at).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })} à {new Date(generated_at).toLocaleTimeString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              <p className="text-xs text-gray-500 mt-2 italic">
                {mentions_legales.usage_pro}
              </p>
            </div>

            {/* Infos établissement */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                Établissement
              </h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Raison sociale :</span>
                  <span className="ml-2 font-medium">{document.raison_sociale}</span>
                </div>
                <div>
                  <span className="text-gray-500">SIRET :</span>
                  <span className="ml-2">{document.siret || '—'}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Adresse :</span>
                  <span className="ml-2">
                    {document.adresse}
                    {document.code_postal && ` - ${document.code_postal}`}
                    {document.ville && ` ${document.ville}`}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Effectif :</span>
                  <span className="ml-2">{document.effectif_total || '—'} personnes</span>
                </div>
                <div>
                  <span className="text-gray-500">Secteur :</span>
                  <span className="ml-2">{document.secteur_activite || '—'}</span>
                </div>
                {document.responsable_nom && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Responsable DUERP :</span>
                    <span className="ml-2">
                      {document.responsable_nom}
                      {document.responsable_fonction && ` (${document.responsable_fonction})`}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Synthèse indicateurs */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                Synthèse
              </h2>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-gray-900">{synthese.nb_unites}</p>
                  <p className="text-sm text-gray-600">Unité(s) de travail</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-2xl font-bold text-gray-900">{synthese.nb_substances}</p>
                  <p className="text-sm text-gray-600">Substance(s)</p>
                </div>
                <div className={`rounded-lg p-4 ${synthese.presence_cmr ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <p className={`text-2xl font-bold ${synthese.presence_cmr ? 'text-red-600' : 'text-gray-900'}`}>
                    {synthese.nb_cmr}
                  </p>
                  <p className="text-sm text-gray-600">CMR identifié(s)</p>
                </div>
              </div>
            </div>

            {/* Alerte CMR */}
            {synthese.presence_cmr && (
              <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">
                  Point de vigilance : {synthese.nb_cmr} substance(s) CMR identifiée(s)
                </p>
                <p className="text-xs text-red-700 mt-1">
                  Suivi individuel renforcé (SIR) obligatoire pour les travailleurs exposés.
                </p>
              </div>
            )}

            {/* Mention légale page 1 */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 italic leading-relaxed">
                {mentions_legales.disclaimer}
              </p>
            </div>

            {/* Pied de page */}
            <div className="mt-8 pt-4 border-t border-gray-100 flex justify-between text-xs text-gray-500">
              <span>ID : {export_id}</span>
              <span>RISK-DATA v{tool_version}</span>
              <span>ID Document : {document.document_id}</span>
            </div>
          </div>
        </div>

        {/* ========== PAGES SUIVANTES: UNITÉS DE TRAVAIL ========== */}
        {document.unites.map((unite, uniteIndex) => (
          <div key={unite.id} className="print-page-break-after bg-white print:shadow-none shadow-lg rounded-lg print:rounded-none mb-8 print:mb-0">
            <div className="p-8 print:p-12">
              {/* En-tête unité */}
              <div className="mb-6 pb-4 border-b border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Unité {uniteIndex + 1} : {unite.nom}
                    </h2>
                    {unite.description && (
                      <p className="text-sm text-gray-600 mt-1">{unite.description}</p>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 text-right">
                    {unite.effectif && <p>{unite.effectif} personne(s)</p>}
                    <p>{unite.substances?.length || 0} substance(s)</p>
                  </div>
                </div>
              </div>

              {/* Liste des substances — 1 bloc complet par substance */}
              {unite.substances && unite.substances.length > 0 && (
                <div className="space-y-6">
                  {unite.substances.map((substance) => {
                    const regl = substance.donnees_reglementaires || {}
                    const hasVLEP = regl.vlep_8h || regl.vlep_court_terme

                    return (
                      <div key={substance.id} className="text-xs border border-gray-200 rounded-lg overflow-hidden">
                        {/* 1️⃣ SYNTHÈSE SUBSTANCE */}
                        <div className={`p-3 ${regl.est_cmr ? 'bg-red-50' : 'bg-white'}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="font-semibold text-gray-900">
                                {substance.nom_substance || substance.cas_number}
                              </p>
                              <p className="text-gray-500 font-mono mt-0.5">
                                CAS {substance.cas_number}
                              </p>
                            </div>
                            {regl.est_cmr && (
                              <span className="inline-block px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                                CMR {regl.categorie_cmr || 'Oui'}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-3 mt-3 text-gray-600">
                            <div>
                              <span className="text-gray-500">Fréquence :</span>{' '}
                              {FREQUENCE_OPTIONS.find((f) => f.value === substance.frequence)?.label || '—'}
                            </div>
                            <div>
                              <span className="text-gray-500">Voies :</span>{' '}
                              {substance.voies_exposition?.map((v) =>
                                VOIES_EXPOSITION_OPTIONS.find((o) => o.value === v)?.label
                              ).filter(Boolean).join(', ') || '—'}
                            </div>
                            <div>
                              <span className="text-gray-500">EPI :</span>{' '}
                              {substance.epi_portes?.map((e) =>
                                EPI_OPTIONS.find((o) => o.value === e)?.label
                              ).filter(Boolean).join(', ') || '—'}
                            </div>
                          </div>
                        </div>

                        {/* 2️⃣ CONTEXTE D'EXPOSITION (déclaratif) */}
                        <div className="bg-gray-50 border-t border-gray-200 p-3">
                          <p className="font-medium text-gray-700 mb-2">Contexte d'exposition</p>
                          <div className="grid grid-cols-2 gap-2 text-gray-600">
                            {substance.activite_tache && (
                              <p><span className="text-gray-500">Activité :</span> {substance.activite_tache}</p>
                            )}
                            {substance.duree_moyenne && (
                              <p><span className="text-gray-500">Durée :</span> {substance.duree_moyenne}</p>
                            )}
                            {substance.epc_existants && substance.epc_existants.length > 0 && (
                              <p><span className="text-gray-500">EPC :</span> {substance.epc_existants.map(e => EPC_OPTIONS.find(o => o.value === e)?.label).filter(Boolean).join(', ')}</p>
                            )}
                            {substance.mesures_prevention && (
                              <p className="col-span-2"><span className="text-gray-500">Mesures :</span> {substance.mesures_prevention}</p>
                            )}
                            {!substance.activite_tache && !substance.duree_moyenne &&
                             (!substance.epc_existants || substance.epc_existants.length === 0) &&
                             !substance.mesures_prevention && (
                              <p className="text-gray-400 italic">Non renseigné</p>
                            )}
                          </div>
                        </div>

                        {/* 3️⃣ DONNÉES RÉGLEMENTAIRES DE RÉFÉRENCE - VLEP */}
                        {hasVLEP && (
                          <div className="bg-blue-50 border-t border-blue-100 p-3">
                            <p className="font-medium text-blue-800 mb-2 flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Données réglementaires de référence
                            </p>
                            <div className="grid grid-cols-2 gap-2 text-blue-700 mb-2">
                              {regl.vlep_8h && (
                                <p>
                                  <span className="text-blue-600">VLEP 8h :</span>{' '}
                                  {regl.vlep_8h.valeur} {regl.vlep_8h.unite}
                                </p>
                              )}
                              {regl.vlep_court_terme && (
                                <p>
                                  <span className="text-blue-600">VLEP court terme :</span>{' '}
                                  {regl.vlep_court_terme.valeur} {regl.vlep_court_terme.unite}
                                </p>
                              )}
                            </div>
                            <p className="text-blue-600 text-[10px] leading-relaxed">
                              Réf. source : {regl.sources?.join(', ') || 'INRS'}{(regl.sources?.includes('INRS') || !regl.sources) ? ' — Édition 2024-01' : ''}
                            </p>
                            <p className="text-blue-500 text-[10px] italic mt-1 leading-relaxed">
                              Information documentaire uniquement. Ces valeurs sont fournies à titre indicatif
                              et ne constituent pas une évaluation de l'exposition. L'interprétation relève
                              de la responsabilité de l'employeur.
                            </p>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Pied de page unité */}
              <div className="mt-8 pt-4 border-t border-gray-100 flex justify-between text-xs text-gray-500">
                <span>Unité {uniteIndex + 1}/{document.unites.length}</span>
                <span>{document.raison_sociale}</span>
                <span>{export_id}</span>
              </div>
            </div>
          </div>
        ))}

        {/* ========== DERNIÈRE PAGE: MENTIONS LÉGALES ========== */}
        <div className="bg-white print:shadow-none shadow-lg rounded-lg print:rounded-none mb-8 print:mb-0">
          <div className="p-8 print:p-12">
            <h2 className="text-lg font-semibold text-gray-800 mb-6 pb-2 border-b border-gray-200">
              Mentions légales et avertissements
            </h2>

            {/* Mention disclaimer */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Nature du document</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                {mentions_legales.disclaimer}
              </p>
            </div>

            {/* Mention multi-substances */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Expositions multiples</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                {mentions_legales.multi_substances}
              </p>
            </div>

            {/* Sources des données */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Informations issues des sources officielles</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                {mentions_legales.donnees_sources}
              </p>
            </div>

            {/* Responsabilités */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Responsabilités</h3>
              <p className="text-xs text-gray-600 leading-relaxed">
                L'employeur demeure seul responsable du Document Unique d'Évaluation des Risques
                Professionnels conformément aux articles L4121-1 et R4121-1 du Code du travail.
                L'utilisation de RISK-DATA ne transfère en aucun cas cette responsabilité à l'éditeur de l'outil.
              </p>
            </div>

            {/* Horodatage final */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                <div>
                  <p><span className="text-gray-500">Identifiant export :</span> {export_id}</p>
                  <p><span className="text-gray-500">Identifiant document :</span> {document.document_id}</p>
                </div>
                <div className="text-right">
                  <p><span className="text-gray-500">Date de génération :</span> {new Date(generated_at).toLocaleString('fr-FR')}</p>
                  <p><span className="text-gray-500">Version outil :</span> RISK-DATA v{tool_version}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Styles d'impression */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .print-page-break-after {
            page-break-after: always;
          }

          .print\\:hidden {
            display: none !important;
          }

          @page {
            size: A4;
            margin: 1.5cm;
          }
        }
      `}</style>
    </>
  )
}
